import React, { createContext, useState, useEffect, useContext } from "react";
import {
  getLocationFromIP,
  getLocationFromBrowser,
  getDetailedAddress,
  loginUser as apiLoginUser,
} from "../service/apiService";

// Tạo Context
const AuthContext = createContext();

// Custom hook để sử dụng AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch location chi tiết (ưu tiên Browser GPS, fallback IP)
  const fetchLocation = async () => {
    try {
      // Thử lấy vị trí từ Browser GPS trước (chính xác hơn)
      try {
        const browserLocation = await getLocationFromBrowser();

        // Format địa chỉ chi tiết: "Phường/Xã, Quận/Huyện, Thành phố"
        const parts = [];
        if (browserLocation.ward) parts.push(browserLocation.ward);
        if (browserLocation.district) parts.push(browserLocation.district);
        if (browserLocation.city) parts.push(browserLocation.city);

        const locationString = parts.join(", ") || "Vị trí không xác định";
        setLocation(locationString);

        return browserLocation;
      } catch (gpsError) {
        console.log(
          "GPS not available, falling back to IP location:",
          gpsError.message
        );

        // Fallback: Dùng IP location
        const locationData = await getLocationFromIP();

        // Thử lấy địa chỉ chi tiết từ tọa độ IP
        try {
          const detailedAddr = await getDetailedAddress(
            locationData.latitude,
            locationData.longitude
          );

          if (detailedAddr) {
            const parts = [];
            if (detailedAddr.ward) parts.push(detailedAddr.ward);
            if (detailedAddr.district) parts.push(detailedAddr.district);
            if (detailedAddr.city) parts.push(detailedAddr.city);

            const locationString =
              parts.join(", ") ||
              `${locationData.city}, ${locationData.region}`;
            setLocation(locationString);

            return { ...locationData, ...detailedAddr };
          }
        } catch (addrError) {
          console.log("Detailed address not available:", addrError.message);
        }

        // Fallback cuối: chỉ city + region
        const locationString = `${locationData.city || ""}, ${
          locationData.region || ""
        }`.trim();
        setLocation(locationString || "Vị trí không xác định");
        return locationData;
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      setLocation("Vị trí không xác định");
      return null;
    }
  };

  // Hàm login
  const login = async (userData) => {
    try {
      // Lưu thông tin vào localStorage
      localStorage.setItem("IuDiToken", userData.jwt);
      localStorage.setItem("UserId", userData.user.UserID);
      localStorage.setItem("UserNameIuDi", userData.user.Username);

      // Lưu toàn bộ user object
      localStorage.setItem("IuDiUser", JSON.stringify(userData.user));

      // Cập nhật state
      setUser(userData.user);
      setIsLoggedIn(true);

      // Fetch location sau khi đăng nhập
      await fetchLocation();

      return true;
    } catch (error) {
      console.error("Error in login:", error);
      return false;
    }
  };

  // Hàm logout
  const logout = () => {
    // Xóa tất cả thông tin khỏi localStorage
    localStorage.removeItem("IuDiToken");
    localStorage.removeItem("UserId");
    localStorage.removeItem("UserNameIuDi");
    localStorage.removeItem("IuDiUser");

    // Reset state
    setUser(null);
    setIsLoggedIn(false);
    setLocation("");
  };

  // Kiểm tra persistent login khi app load
  useEffect(() => {
    const checkPersistentLogin = async () => {
      try {
        const token = localStorage.getItem("IuDiToken");
        const userDataString = localStorage.getItem("IuDiUser");

        if (token && userDataString) {
          const userData = JSON.parse(userDataString);
          setUser(userData);
          setIsLoggedIn(true);

          // Fetch location cho persistent login
          await fetchLocation();
        }
      } catch (error) {
        console.error("Error checking persistent login:", error);
        // Nếu có lỗi, clear localStorage
        logout();
      } finally {
        setLoading(false);
      }
    };

    checkPersistentLogin();
  }, []);

  // Update user info
  const updateUser = (updatedUserData) => {
    setUser((prev) => ({ ...prev, ...updatedUserData }));
    localStorage.setItem(
      "IuDiUser",
      JSON.stringify({ ...user, ...updatedUserData })
    );
  };

  const value = {
    user,
    isLoggedIn,
    location,
    loading,
    login,
    logout,
    fetchLocation,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
